import React, { useState, useEffect } from "react";
import { useTable } from "react-table";
import { db } from "../Firebase"; // Import Firestore instance from Firebase
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import "./Admin.css";

function Admin() {
  const [data, setData] = useState([]);
  const [newProperties, setNewProperties] = useState([]); // Local state for new properties
  const [saveSuccess, setSaveSuccess] = useState(false); // State for showing success notification
  const navigate = useNavigate(); // Initialize useNavigate for navigation

  useEffect(() => {
    // Fetch properties from Firebase and recalculate Weeks on Market on component mount
    const fetchAndRecalculateProperties = async () => {
      const querySnapshot = await getDocs(collection(db, "properties"));
      const properties = querySnapshot.docs.map((doc) => {
        const propertyData = doc.data();
        const weeksOnMarket = calculateWeeksOnMarket(
          propertyData.instructionDate
        ); // Calculate weeks on market
        return {
          id: doc.id,
          ...propertyData,
          weeksOnMarket, // Add calculated weeks on market
        };
      });

      setData(properties);
    };

    fetchAndRecalculateProperties();
  }, []);

  // Calculate Weeks on Market based on the instruction date
  const calculateWeeksOnMarket = (instructionDate) => {
    if (!instructionDate) return 0;

    // Convert date assuming DD/MM/YYYY format
    const parts = instructionDate.split("/");
    const formattedDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`); // Rearrange for JS Date
    if (isNaN(formattedDate)) {
      console.error(
        "Invalid date format for instruction date:",
        instructionDate
      );
      return 0;
    }

    const today = new Date();
    const timeDifference = today - formattedDate;
    const weeksOnMarket = Math.ceil(timeDifference / (1000 * 60 * 60 * 24 * 7)); // Calculate weeks
    return weeksOnMarket > 0 ? weeksOnMarket : 0;
  };

  // Function to format the price as £X,XXX,XXX
  const formatPrice = (price) => {
    if (price) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
      }).format(price);
    }
    return "£0";
  };

  // Add property handler (only adds an empty row locally)
  const addProperty = () => {
    const newProperty = {
      houseNumber: "",
      roadName: "",
      town: "Marlow",
      county: "Buckinghamshire",
      postcode: "",
      askingPrice: 0,
      agent: "",
      instructionDate: "", // New instruction date field
      status: "Available", // New status field defaulted to "Available"
      rightmoveLink: "", // New field for Rightmove link
      notes: "",
      weeksOnMarket: 0, // Initially set weeks on market to 0
    };
    setNewProperties([...newProperties, newProperty]);
    setSaveSuccess(false); // Reset success notification when adding a new property
  };

  // Save new properties to Firestore
  const saveProperties = async () => {
    try {
      // Handle saving for both new and existing properties
      const allProperties = [...data, ...newProperties];

      for (const property of allProperties) {
        const weeksOnMarket = calculateWeeksOnMarket(property.instructionDate);

        if (property.id) {
          // Update existing property
          const propertyRef = doc(db, "properties", property.id);
          await updateDoc(propertyRef, {
            ...property,
            weeksOnMarket, // Update weeks on market value
          });
        } else {
          // Add new property
          const docRef = await addDoc(collection(db, "properties"), {
            ...property,
            weeksOnMarket, // Save weeks on market value
          });
          setData((oldData) => [
            ...oldData,
            { id: docRef.id, ...property, weeksOnMarket },
          ]);
        }
      }

      // Clear new properties after saving
      setNewProperties([]);
      setSaveSuccess(true); // Show success notification
    } catch (error) {
      console.error("Error saving properties: ", error);
      alert("Failed to save properties: " + error.message);
    }
  };

  // Update property handler (for local updates and Firestore updates)
  const updateData = async (rowIndex, columnId, value) => {
    let updatedData;
    if (rowIndex < data.length) {
      updatedData = data.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...row,
            [columnId]: value,
          };
        }
        return row;
      });
      setData(updatedData);

      // Update the property in Firestore
      const propertyToUpdate = updatedData[rowIndex];
      if (propertyToUpdate.id) {
        const propertyRef = doc(db, "properties", propertyToUpdate.id);
        await updateDoc(propertyRef, { [columnId]: value });
      }
    } else {
      updatedData = newProperties.map((row, index) => {
        if (index === rowIndex - data.length) {
          return {
            ...row,
            [columnId]: value,
          };
        }
        return row;
      });
      setNewProperties(updatedData);
    }
  };

  // Delete property handler
  const deleteProperty = async (propertyId) => {
    try {
      await deleteDoc(doc(db, "properties", propertyId));
      setData((oldData) =>
        oldData.filter((property) => property.id !== propertyId)
      );
    } catch (error) {
      console.error("Error deleting property: ", error);
      alert("Failed to delete property: " + error.message);
    }
  };

  // Define table columns
  const columns = React.useMemo(
    () => [
      {
        Header: "House Name/Number",
        accessor: "houseNumber",
        Cell: EditableCell,
      },
      {
        Header: "Road Name",
        accessor: "roadName",
        Cell: EditableCell,
      },
      {
        Header: "Town",
        accessor: "town",
        Cell: EditableCell,
      },
      {
        Header: "County",
        accessor: "county",
        Cell: EditableCell,
      },
      {
        Header: "Postcode",
        accessor: "postcode",
        Cell: EditableCell,
      },
      {
        Header: "Asking Price",
        accessor: "askingPrice",
        Cell: ({ value, row: { index }, column: { id }, updateData }) => {
          const [localValue, setLocalValue] = useState(
            value ? formatPrice(value) : ""
          );

          const handleBlur = () => {
            const rawValue = parseFloat(localValue.replace(/[£,]/g, "")); // Strip £ and commas before saving to Firestore
            updateData(index, id, rawValue); // Save the raw value to Firestore
          };

          return (
            <input
              type="text"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onBlur={handleBlur}
              placeholder="£0"
            />
          );
        },
      },
      {
        Header: "Agent",
        accessor: "agent",
        Cell: EditableCell,
      },
      {
        Header: "Instruction Date",
        accessor: "instructionDate",
        Cell: ({ value, row: { index }, column: { id }, updateData }) => {
          const [localValue, setLocalValue] = useState(value);

          const handleBlur = () => {
            updateData(index, id, localValue);
          };

          return (
            <input
              type="text"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onBlur={handleBlur}
              placeholder="DD/MM/YYYY"
            />
          );
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value, row: { index }, column: { id }, updateData }) => {
          const handleChange = (e) => {
            updateData(index, id, e.target.value);
          };

          return (
            <select value={value} onChange={handleChange}>
              <option value="Available">Available</option>
              <option value="Under Offer">Under Offer</option>
              <option value="Fall Through">Fall Through</option>
              <option value="Removed">Removed</option>
            </select>
          );
        },
      },
      {
        Header: "Weeks on Market",
        accessor: "weeksOnMarket",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Rightmove Link",
        accessor: "rightmoveLink",
        Cell: ({ value, row, column: { id }, updateData }) => {
          const [localValue, setLocalValue] = useState(value);

          const handleBlur = () => {
            updateData(row.index, id, localValue);
          };

          return row.original.id ? (
            <a href={value} target="_blank" rel="noopener noreferrer">
              View Listing
            </a>
          ) : (
            <input
              type="text"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onBlur={handleBlur}
              placeholder="Rightmove URL"
            />
          );
        },
      },
      {
        Header: "Notes",
        accessor: "notes",
        Cell: EditableCell,
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <button
            onClick={() => deleteProperty(row.original.id)}
            className="delete-property-button"
          >
            Delete
          </button>
        ),
      },
    ],
    [data]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data: [...data, ...newProperties], // Display both saved and new properties
      defaultColumn: {
        Cell: EditableCell,
      },
      updateData,
    });

  // Editable cell rendering
  function EditableCell({
    value: initialValue,
    row: { index },
    column: { id },
    updateData,
  }) {
    const [localValue, setLocalValue] = useState(initialValue);

    const handleBlur = () => {
      updateData(index, id, localValue);
    };

    return (
      <input
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
      />
    );
  }

  // Handle navigation to the dashboard
  const handleSwitchToDashboard = () => {
    navigate("/dashboard");
  };

  return (
    <div>
      <h1>Admin Page</h1>
      <p>This page is for admin users to manage property data.</p>

      {/* Switch to Dashboard Button */}
      <button
        onClick={handleSwitchToDashboard}
        className="switch-dashboard-button"
      >
        Switch to Dashboard
      </button>

      <button onClick={addProperty} className="add-property-button">
        Add Property
      </button>
      <button onClick={saveProperties} className="save-property-button">
        Save Properties
      </button>
      {saveSuccess && (
        <p className="success-message">Properties saved successfully!</p>
      )}
      <table {...getTableProps()} className="property-table">
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()}>{column.render("Header")}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default Admin;
